import { useState, useRef, useCallback, useEffect } from "react";
import { useMicPermission, type MicPermission } from "./useMicPermission";

export type { MicPermission } from "./useMicPermission";
export type MediaRecorderState = "idle" | "recording";

const MIME_TYPE_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
] as const;

let sharedAudioContext: AudioContext | null = null;
export function getSharedAudioContext(): AudioContext | null {
  if (typeof AudioContext === "undefined") return null;
  sharedAudioContext ??= new AudioContext();
  return sharedAudioContext;
}

function detectSupportedMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const mimeType of MIME_TYPE_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }
  return null;
}

interface UseMediaRecorderResult {
  state: MediaRecorderState;
  stream: MediaStream | null;
  isSupported: boolean;
  micPermission: MicPermission;
  startRecording: (onPermissionDenied?: () => void) => void;
  stopRecording: (onStop: (blob: Blob, mimeType: string) => void) => void;
  cancelRecording: () => void;
}

export function useMediaRecorder(): UseMediaRecorderResult {
  const [state, setState] = useState<MediaRecorderState>("idle");
  const [{ isSupported, mimeType: initialMimeType }] = useState(() => {
    const mimeType = detectSupportedMimeType();
    const isSupported =
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia &&
      mimeType !== null;
    return { isSupported, mimeType };
  });
  const permissionFromApi = useMicPermission();
  const [deniedByGetUserMedia, setDeniedByGetUserMedia] =
    useState<boolean>(false);
  const micPermission: MicPermission = deniedByGetUserMedia
    ? "denied"
    : permissionFromApi;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string | null>(initialMimeType);
  const streamRef = useRef<MediaStream | null>(null);
  const pendingStartAbortRef = useRef<AbortController | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const stopCurrentStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const resetRecorderState = useCallback(() => {
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setState("idle");
  }, []);

  const startRecording = useCallback(
    (onPermissionDenied?: () => void) => {
      pendingStartAbortRef.current?.abort();
      const abortController = new AbortController();
      pendingStartAbortRef.current = abortController;
      const { signal } = abortController;

      void (async () => {
        try {
          setState("recording");
          await getSharedAudioContext()?.resume();

          let stream = streamRef.current;
          const tracksAlive =
            stream !== null &&
            stream.getAudioTracks().every((t) => t.readyState !== "ended");

          if (!tracksAlive) {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            if (signal.aborted) {
              stream.getTracks().forEach((t) => t.stop());
              pendingStartAbortRef.current = null;
              return;
            }

            streamRef.current = stream;
            setStream(stream);
          }

          pendingStartAbortRef.current = null;

          if (!stream) {
            setState("idle");
            return;
          }

          const mimeType = mimeTypeRef.current;
          let mediaRecorder: MediaRecorder;
          try {
            mediaRecorder = mimeType
              ? new MediaRecorder(stream, { mimeType })
              : new MediaRecorder(stream);
          } catch {
            stopCurrentStream();
            setState("idle");
            onPermissionDenied?.();
            return;
          }

          mediaRecorderRef.current = mediaRecorder;
          chunksRef.current = [];

          mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) {
              chunksRef.current.push(e.data);
            }
          };

          mediaRecorder.start();
        } catch {
          pendingStartAbortRef.current = null;
          stopCurrentStream();
          setState("idle");
          setDeniedByGetUserMedia(true);
          onPermissionDenied?.();
        }
      })();
    },
    [stopCurrentStream],
  );

  const stopRecording = useCallback(
    (onStop: (blob: Blob, mimeType: string) => void) => {
      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder || mediaRecorder.state === "inactive") {
        setState("idle");
        return;
      }

      mediaRecorder.onstop = () => {
        const mimeType = mimeTypeRef.current ?? "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        setState("idle");
        onStop(blob, mimeType);
      };

      mediaRecorder.stop();
    },
    [],
  );

  const cancelRecording = useCallback(() => {
    pendingStartAbortRef.current?.abort();
    pendingStartAbortRef.current = null;

    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.onstop = () => {
        stopCurrentStream();
        resetRecorderState();
      };
      mediaRecorder.stop();
    } else {
      stopCurrentStream();
    }
    setState("idle");
  }, [stopCurrentStream, resetRecorderState]);

  useEffect(() => {
    return () => {
      pendingStartAbortRef.current?.abort();
      pendingStartAbortRef.current = null;

      const mediaRecorder = mediaRecorderRef.current;
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.onstop = () => {
          stopCurrentStream();
        };
        mediaRecorder.stop();
      } else {
        stopCurrentStream();
      }
    };
  }, [stopCurrentStream]);

  return {
    state,
    stream,
    isSupported,
    micPermission,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
